import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { Link } from './ui/link';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { SectionHubEmptyIllustration } from './SectionHubEmptyIllustration';
import { useLocale } from '../contexts/LocaleContext';
import { cn } from '../lib/utils';

export interface SectionHubItem {
  id: string;
  label: string;
  href?: string;
}

interface SectionHubCardProps {
  title: string;
  icon: LucideIcon;
  href: string;
  emptyDescription: string;
  items: SectionHubItem[];
  itemsHeading?: string;
  className?: string;
}

export const SectionHubCard: React.FC<SectionHubCardProps> = ({
  title,
  icon: Icon,
  href,
  emptyDescription,
  items,
  itemsHeading,
  className,
}) => {
  const { t } = useLocale();
  const isEmpty = items.length === 0;

  return (
    <Card
      className={cn(
        'flex h-full min-h-[220px] flex-col overflow-hidden shadow-none transition-colors',
        isEmpty && 'hover:border-foreground/20 hover:bg-accent/30',
        className
      )}
    >
      <CardHeader className="flex flex-row items-center gap-2 space-y-0 p-4 pb-2">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <CardTitle className="text-base font-semibold">
          {isEmpty ? (
            <Link to={href} className="text-foreground hover:text-foreground">
              {title}
            </Link>
          ) : (
            title
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col p-4 pt-2">
        {isEmpty ? (
          <Link
            to={href}
            className="flex flex-1 flex-col items-center justify-center rounded-md px-2 py-4 text-center text-foreground hover:text-foreground"
          >
            <SectionHubEmptyIllustration icon={Icon} />
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">{emptyDescription}</p>
          </Link>
        ) : (
          <>
            {itemsHeading ? (
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {itemsHeading}
              </h3>
            ) : null}
            <ul className="flex flex-1 flex-col gap-1.5">
              {items.map((item) => {
                const itemHref = item.href ?? href;
                return (
                  <li key={item.id}>
                    <Link
                      to={itemHref}
                      className="block truncate rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Link
              to={href}
              className="mt-3 inline-flex items-center gap-1 self-start text-sm font-medium text-primary hover:text-primary/80"
            >
              {t('section_hub_go_to').replace('{section}', title)}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
};
